/**
 * <blog-search> — Live-Suche innerhalb eines Blogs.
 *
 * Nutzt Shopifys Predictive-Search-API (resources[type]=article) und grenzt die
 * Treffer clientseitig auf den aktuellen Blog ein (Attribut `blog-handle`), damit
 * die Suche wirklich nur diesen Blog abdeckt.
 *
 * Erwartete Attribute:
 *   blog-handle  – Handle des aktuellen Blogs (z. B. "news")
 *   search-url   – Basis-URL der Predictive-Search (routes.predictive_search_url)
 *
 * Erwartete refs (data-ref bzw. ref) im Markup:
 *   form, input, clear, results, status
 */
class BlogSearch extends HTMLElement {
  constructor() {
    super();
    this.debounceMs = 250;
    this.minChars = 2;
    this.limit = 10;
    this.timer = null;
    this.controller = null;
    this.highlightIndex = -1;
    this.results = [];
  }

  connectedCallback() {
    this.blogHandle = this.getAttribute('blog-handle') || '';
    const base = this.getAttribute('search-url') || '/search/suggest';
    this.endpoint = base.endsWith('.json') ? base : `${base}.json`;

    this.form = this.query('form');
    this.input = this.query('input');
    this.clearBtn = this.query('clear');
    this.resultsEl = this.query('results');
    this.statusEl = this.query('status');

    if (!this.input || !this.resultsEl) return;

    this.form?.addEventListener('submit', this.onSubmit.bind(this));
    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.clearBtn?.addEventListener('click', this.reset.bind(this));

    // Dropdown schließen bei Klick außerhalb / Escape verlässt Fokus
    this.onDocClick = (e) => {
      if (!this.contains(e.target)) this.close();
    };
    document.addEventListener('click', this.onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    clearTimeout(this.timer);
    this.controller?.abort();
  }

  query(name) {
    return this.querySelector(`[ref="${name}"]`);
  }

  onSubmit(e) {
    e.preventDefault();
    const links = this.resultsEl.querySelectorAll('.blog-search__result');
    // Treffer offen → hervorgehobenen bzw. ersten Treffer öffnen
    if (!this.resultsEl.hidden && links.length) {
      (links[this.highlightIndex] || links[0]).click();
      return;
    }
    // Sonst sofort suchen (Debounce überspringen)
    const value = this.input.value.trim();
    if (value.length >= this.minChars) {
      clearTimeout(this.timer);
      this.search(value);
    }
    this.input.focus();
  }

  onInput() {
    const value = this.input.value.trim();
    this.clearBtn.hidden = value.length === 0;
    clearTimeout(this.timer);

    if (value.length < this.minChars) {
      this.close();
      return;
    }
    this.timer = setTimeout(() => this.search(value), this.debounceMs);
  }

  onKeydown(e) {
    if (this.resultsEl.hidden) return;
    const links = this.resultsEl.querySelectorAll('.blog-search__result');

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.move(1, links);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.move(-1, links);
        break;
      case 'Enter':
        if (this.highlightIndex >= 0 && links[this.highlightIndex]) {
          e.preventDefault();
          links[this.highlightIndex].click();
        }
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  move(dir, links) {
    if (!links.length) return;
    links.forEach((l) => l.classList.remove('is-highlighted'));
    this.highlightIndex =
      (this.highlightIndex + dir + links.length) % links.length;
    const active = links[this.highlightIndex];
    active.classList.add('is-highlighted');
    active.scrollIntoView({ block: 'nearest' });
  }

  async search(query) {
    this.controller?.abort();
    this.controller = new AbortController();

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'article',
      'resources[limit]': String(this.limit),
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,body,tag',
    });

    this.renderMessage('Suche läuft …');

    try {
      const res = await fetch(`${this.endpoint}?${params}`, {
        signal: this.controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const articles = (data?.resources?.results?.articles || []).filter(
        (a) => this.belongsToBlog(a.url)
      );
      this.results = articles;
      this.render(articles, query);
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.renderMessage('Suche gerade nicht möglich. Bitte erneut versuchen.');
    }
  }

  /** Nur Treffer aus diesem Blog: /blogs/<handle>/<article> */
  belongsToBlog(url) {
    if (!this.blogHandle || !url) return true;
    return url.split('?')[0].includes(`/blogs/${this.blogHandle}/`);
  }

  render(articles, query) {
    this.highlightIndex = -1;

    if (!articles.length) {
      this.renderMessage(`Keine Artikel zu „${query}“ gefunden.`);
      this.announce('Keine Ergebnisse');
      return;
    }

    this.resultsEl.innerHTML = articles
      .map((a) => this.resultMarkup(a, query))
      .join('');
    this.open();
    this.announce(
      `${articles.length} ${articles.length === 1 ? 'Ergebnis' : 'Ergebnisse'}`
    );
  }

  resultMarkup(article, query) {
    const title = this.highlight(article.title || '', query);
    const image = article.image?.url || article.image || '';
    const thumb = image
      ? `<img class="blog-search__result-thumb" src="${this.escape(
          image
        )}" alt="" loading="lazy" width="48" height="48">`
      : '';
    return `<a class="blog-search__result" role="option" href="${this.escape(
      article.url
    )}">${thumb}<span class="blog-search__result-title">${title}</span></a>`;
  }

  highlight(text, query) {
    const safe = this.escape(text);
    const q = query.trim();
    if (!q) return safe;
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return safe.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>');
  }

  renderMessage(msg) {
    this.resultsEl.innerHTML = `<p class="blog-search__message">${this.escape(
      msg
    )}</p>`;
    this.open();
  }

  open() {
    this.resultsEl.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.resultsEl.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.highlightIndex = -1;
  }

  reset() {
    this.input.value = '';
    this.clearBtn.hidden = true;
    this.close();
    this.input.focus();
  }

  announce(text) {
    if (this.statusEl) this.statusEl.textContent = text;
  }

  escape(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('blog-search')) {
  customElements.define('blog-search', BlogSearch);
}
